import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { AppDispatch, RootState, useAppDispatch } from '../../../redux/store'
import { fetchSecurityCheckFamilyByCategory } from '../../../redux/securityCheckFamily/securityCheckFamilyAction'
import { resetSecurityCheckFamily } from '../../../redux/securityCheckFamily/securityCheckFamilySlice'
import { selectSpecifications } from '../../../redux/specification/specificationSelectors'
import { formatNormLabel } from '../../../utils/normLabels'
import { formatTitle } from '../../../utils/formatTitle'
import { writeSecurityNav } from '../../../utils/securityNav'
import { SecurityCheckFamily } from '../../../types/securityCheckFamilysTypes'
import { ListPageDescription } from '../../../components/common/ListPageDescription'
import '../../../components/common/ListPageTable.css'
import TableEmptyState from '../../../components/common/TableEmptyState'
import i18n from '../../../i18n'
import './DashboardMappingControls.css'
import { formatDecimal } from '../../../utils/formatNumbers'

const FALLBACK_FRAMEWORKS = ['NIST', 'NIS2', 'ISO_22', 'SOC', 'SWIFT']

type Props = {
  onFamilyCountChange?: (count: number) => void
}

function mapBarColor(pct: number) {
  if (pct >= 70) return '#1FBFA3'
  if (pct >= 40) return '#B0730F'
  return '#D14A3E'
}

/** Stable display ratio for mapping progress when no mapping API exists. */
function mappingRatio(family: SecurityCheckFamily): { mapped: number; total: number } {
  const score = Number(family.SCFScore ?? family.scfScore ?? 0)
  const total = 5
  const mapped = Math.max(0, Math.min(total, Math.round(score)))
  return { mapped, total }
}

export default function DashboardMappingControls({
  onFamilyCountChange,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()

  const specs = useSelector(selectSpecifications)[0]
  const baseNorm = specs?.NormBase || ''
  const available = (specs?.IndustrialApplicableFrameworks || []) as string[]

  const frameworks = useMemo(() => {
    const set = new Set<string>()
    if (baseNorm) set.add(baseNorm)
    available.forEach((n) => n && set.add(n))
    if (set.size === 0) {
      FALLBACK_FRAMEWORKS.forEach((n) => set.add(n))
    }
    return Array.from(set)
  }, [baseNorm, available])

  const [activeFw, setActiveFw] = useState('')
  const [search, setSearch] = useState('')
  const [switching, setSwitching] = useState(false)

  useEffect(() => {
    if (!activeFw && frameworks.length) {
      setActiveFw(
        frameworks.includes(baseNorm) ? baseNorm : frameworks[0]
      )
    }
  }, [frameworks, baseNorm, activeFw])

  const { SecurityCheckFamilyResponse, loading } = useSelector(
    (state: RootState) => state.securitycheckFamily
  )

  useEffect(() => {
    if (!activeFw) return
    let cancelled = false
    setSwitching(true)
    setSearch('')
    writeSecurityNav({ norm: activeFw })
    dispatch(resetSecurityCheckFamily())

    const action = fetchSecurityCheckFamilyByCategory({
      category: activeFw,
      page: 1,
      pageSize: 100,
      language: i18n.language.toLocaleUpperCase(),
    })

    if (action) {
      dispatch(action).finally(() => {
        if (!cancelled) setSwitching(false)
      })
    } else if (!cancelled) {
      setSwitching(false)
    }

    return () => {
      cancelled = true
    }
  }, [dispatch, activeFw, i18n.language])

  const isBusy = switching || loading
  const families = isBusy
    ? []
    : SecurityCheckFamilyResponse?.securityCheckFamilies || []

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return families
    return families.filter((f) => {
      const label = (f.LabelSCF || '').toLowerCase()
      const source = (f.Source || '').toLowerCase()
      return label.includes(q) || source.includes(q)
    })
  }, [families, search])

  useEffect(() => {
    if (!isBusy) onFamilyCountChange?.(families.length)
  }, [families.length, onFamilyCountChange, isBusy])

  const selectFramework = (fw: string) => {
    if (fw === activeFw) return
    setActiveFw(fw)
  }

  const fwLabel = (key: string) =>
    t(`norms.${key}.title`, { defaultValue: formatNormLabel(key) })

  const activeLabel = fwLabel(activeFw || baseNorm)

  const kpis = useMemo(() => {
    const total = families.length
    let mappedSum = 0
    let mappedCap = 0
    let awaiting = 0
    let scoreSum = 0
    families.forEach((f) => {
      const { mapped, total: cap } = mappingRatio(f)
      mappedSum += mapped
      mappedCap += cap
      const score = Number(f.SCFScore ?? f.scfScore ?? 0)
      scoreSum += score
      if (score < 1) awaiting += 1
    })
    const coverage =
      total === 0 ? 0 : Math.round((scoreSum / (total * 5)) * 100)
    return {
      families: total,
      mappedSum,
      mappedCap: mappedCap || 1,
      awaiting,
      coverage: Math.min(100, Math.max(0, coverage)),
    }
  }, [families])

  const openFamily = (family: SecurityCheckFamily) => {
    if (!family._id) return
    const label = family.LabelSCF
      ? formatTitle(family.LabelSCF)
      : undefined
    writeSecurityNav({
      norm: activeFw,
      familyId: family._id,
      familyLabel: label,
    })
    navigate(`/security-controls/${family._id}`, {
      state: { familyId: family._id, familyLabel: label, norm: activeFw },
    })
  }

  const openMapping = (family?: SecurityCheckFamily) => {
    const familyId = family?._id || family?.SecurityId
    if (familyId) {
      navigate(`/securityControlsToMap/NIST/${familyId}`)
      return
    }
    navigate('/securityFramework')
  }

  const mapTargetLabel =
    activeFw === 'NIST' || activeFw === 'NIST SP 800-53'
      ? t('homePage.mappingTab.viewControls', { defaultValue: 'Open →' })
      : t('homePage.mappingTab.mapToNist', { defaultValue: 'Map to NIST →' })

  return (
    <div className="dmap">
      <div className="dmap-fw-switch">
        <span className="dmap-eyebrow">
          {t('homePage.mappingTab.framework', { defaultValue: 'Framework' })}
        </span>
        {frameworks.map((fw) => (
          <button
            key={fw}
            type="button"
            className={`dmap-chip${activeFw === fw ? ' on' : ''}`}
            disabled={isBusy && activeFw === fw}
            onClick={() => selectFramework(fw)}
          >
            {fwLabel(fw)}
          </button>
        ))}
      </div>

      <div className={`dmap-body${isBusy ? ' is-loading' : ''}`}>
        {isBusy && (
          <div className="dmap-loader" role="status" aria-live="polite">
            <div className="dmap-spinner" />
            <span>
              {t('homePage.mappingTab.loadingFramework', {
                norm: activeLabel,
                defaultValue: `Loading ${activeLabel}…`,
              })}
            </span>
          </div>
        )}

      <div className="dmap-kpi-grid">
        <div className="dmap-kpi">
          <div className="dmap-eyebrow">
            {t('homePage.mappingTab.families', { defaultValue: 'Families' })}
          </div>
          <div className="dmap-kpi-v">{kpis.families}</div>
          <div className="dmap-kpi-d">
            {t('homePage.mappingTab.loadedFor', {
              norm: activeLabel,
              defaultValue: `Loaded for ${activeLabel}`,
            })}
          </div>
        </div>
        <div className="dmap-kpi">
          <div className="dmap-eyebrow">
            {t('homePage.mappingTab.mappedToNist', {
              defaultValue: 'Mapped to NIST',
            })}
          </div>
          <div className="dmap-kpi-v dmap-kpi-v--teal">
            {kpis.mappedSum}{' '}
            <span className="dmap-kpi-of">
              {t('homePage.mappingTab.of', { defaultValue: 'of' })} {kpis.mappedCap}
            </span>
          </div>
          <div className="dmap-kpi-d">
            {t('homePage.mappingTab.mappedDesc', {
              defaultValue: 'Requirements already connected',
            })}
          </div>
        </div>
        <div className="dmap-kpi">
          <div className="dmap-eyebrow">
            {t('homePage.mappingTab.awaiting', {
              defaultValue: 'Awaiting review',
            })}
          </div>
          <div className="dmap-kpi-v dmap-kpi-v--amber">{kpis.awaiting}</div>
          <div className="dmap-kpi-d">
            {t('homePage.mappingTab.awaitingDesc', {
              defaultValue: 'Suggested links needing confirmation',
            })}
          </div>
        </div>
        <div className="dmap-kpi">
          <div className="dmap-eyebrow">
            {t('homePage.mappingTab.coverage', { defaultValue: 'Coverage' })}
          </div>
          <div className="dmap-kpi-v">{kpis.coverage} %</div>
          <div className="dmap-bar-track">
            <span
              style={{
                width: `${kpis.coverage}%`,
                background: '#1FBFA3',
              }}
            />
          </div>
          <div className="dmap-kpi-d">
            {t('homePage.mappingTab.coverageDesc', {
              norm: activeLabel,
              defaultValue: `Of ${activeLabel} met by existing controls`,
            })}
          </div>
        </div>
      </div>

      <div className="dmap-card">
        <div className="dmap-toolbar">
          <span className="dmap-search">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
              <circle cx="7" cy="7" r="4.5" />
              <path d="M10.5 10.5L14 14" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('homePage.mappingTab.filterFamily', {
                defaultValue: 'Filter by family',
              })}
            />
          </span>
          <button type="button" className="dmap-chip dmap-chip--ghost">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
              <path d="M2.5 4.5h11M4.5 8h7M6.5 11.5h3" />
            </svg>
            {t('homePage.mappingTab.filters', { defaultValue: 'Filters' })}
          </button>
          <span className="dmap-count-note">
            {t('homePage.mappingTab.listNote', {
              norm: activeLabel,
              count: filtered.length,
              defaultValue: `${activeLabel} Security Controls Families List · ${filtered.length} families`,
            })}
          </span>
        </div>

        <div className="dmap-table-wrap">
          {isBusy ? (
            <div className="dmap-empty dmap-empty--tall" aria-hidden />
          ) : filtered.length === 0 ? (
            <div className="dmap-empty">
              <TableEmptyState compact />
            </div>
          ) : (
            <table className="dmap-table">
              <thead>
                <tr>
                  <th>{t('Label')}</th>
                  <th>{t('Source')}</th>
                  <th>{t('listPages.score', { defaultValue: 'Score' })}</th>
                  <th>
                    {t('homePage.mappingTab.mappedToNist', {
                      defaultValue: 'Mapped to NIST',
                    })}
                  </th>
                  <th style={{ textAlign: 'right' }}>
                    {t('Actions', { defaultValue: 'Action' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((family) => {
                  const scoreRaw = Number(family.SCFScore ?? family.scfScore)
                  const score =
                    !Number.isNaN(scoreRaw) && family.SCFScore != null
                      ? formatDecimal(scoreRaw)
                      : '—'
                  const { mapped, total } = mappingRatio(family)
                  const pct = Math.round((mapped / total) * 100)
                  const label = family.LabelSCF
                    ? formatTitle(family.LabelSCF)
                    : '—'

                  return (
                    <tr
                      key={family._id}
                      onClick={() => openFamily(family)}
                    >
                      <td className="dmap-label">
                        <ListPageDescription
                          value={label === '—' ? undefined : label}
                          tone="primary"
                        />
                      </td>
                      <td>
                        <span className="dmap-mono">{family.Source || '—'}</span>
                      </td>
                      <td>
                        <span className="dmap-mono">{score}</span>
                      </td>
                      <td>
                        <div className="dmap-map-cell">
                          <span className="dmap-bar-track dmap-bar-track--sm">
                            <span
                              style={{
                                width: `${pct}%`,
                                background: mapBarColor(pct),
                              }}
                            />
                          </span>
                          <span className="dmap-mono dmap-map-frac">
                            {mapped}/{total}
                          </span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="dmap-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            openMapping(family)
                          }}
                        >
                          {mapTargetLabel}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="dmap-pager">
          <span className="dmap-pager-info">
            {t('homePage.mappingTab.showing', {
              shown: filtered.length,
              total: families.length,
              defaultValue: `Showing ${filtered.length} of ${families.length}`,
            })}
          </span>
          <button
            type="button"
            className="dmap-btn"
            onClick={() => navigate('/securityFramework')}
          >
            {t('homePage.openMapping', {
              defaultValue: 'Open framework mapping',
            })}
          </button>
        </div>
      </div>
      </div>
    </div>
  )
}
